import { FC } from "react";
import { StepsProps } from "../../types";
import { Button, Card, CardContent } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep3 from "./useStep3";
import { FormCheckbox, FormSectionHeader, FormSelect, FormSwitch, Loader } from "@/components";
import {
  ADS,
  CONSENT_ENABLED,
  DISPLAY_EMAIL,
  DISPLAY_LANDLINE,
  DISPLAY_MOBILE,
  DISPLAY_NAME,
  NEIGHBOR_PROPERTY_ID,
  OPT_EMERGENCY,
  OPT_GENERAL,
  PARTNER_EMAIL,
  PROP_ADDRESS,
  PROPERTY_ID,
  SMS_PAST_DUE,
} from "./consts";
import { Info, Lightbulb, ShieldCheck, UsersRound } from "lucide-react";
import moment from "moment";

const Step3: FC<StepsProps> = ({ onNextClick, onTermsConditionClick }) => {
  const {
    form,
    onSubmit,
    requiredFields,
    isUpdatePreferencePending,
    isLoading,
    myProperties,
    neighborProperties,
    propertyId,
    isLoadingNeighborProperties,
    neighborKey,
    onChangeNeighbor,
    isLoadingConsentDtl,
    consentDtl,
    formData
  } = useStep3({ onNextClick });
  const { errors } = form.formState;

  return isLoading ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <div>
            <FormSectionHeader>User Preference</FormSectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
              <FormCheckbox
                name={SMS_PAST_DUE}
                control={form.control}
                label="SMS for Past Due"
                error={errors[SMS_PAST_DUE]?.message}
                isRequired={requiredFields.includes(SMS_PAST_DUE)}
              />
              <FormCheckbox
                name={OPT_GENERAL}
                control={form.control}
                label="Opt In General"
                error={errors[OPT_GENERAL]?.message}
                isRequired={requiredFields.includes(OPT_GENERAL)}
              />
              <FormCheckbox
                name={OPT_EMERGENCY}
                control={form.control}
                label="Opt In Emergency"
                error={errors[OPT_EMERGENCY]?.message}
                isRequired={requiredFields.includes(OPT_EMERGENCY)}
              />
              <FormCheckbox
                name={DISPLAY_NAME}
                control={form.control}
                label="Display Name"
                error={errors[DISPLAY_NAME]?.message}
                isRequired={requiredFields.includes(DISPLAY_NAME)}
              />
              <FormCheckbox
                name={PROP_ADDRESS}
                control={form.control}
                label="Display Prop Address"
                error={errors[PROP_ADDRESS]?.message}
                isRequired={requiredFields.includes(PROP_ADDRESS)}
              />
              <FormCheckbox
                name={DISPLAY_EMAIL}
                control={form.control}
                label="Display Email Address"
                error={errors[DISPLAY_EMAIL]?.message}
                isRequired={requiredFields.includes(DISPLAY_EMAIL)}
              />
              <FormCheckbox
                name={DISPLAY_MOBILE}
                control={form.control}
                label="Display Mobile"
                error={errors[DISPLAY_MOBILE]?.message}
                isRequired={requiredFields.includes(DISPLAY_MOBILE)}
              />
              <FormCheckbox
                name={DISPLAY_LANDLINE}
                control={form.control}
                label="Display Landline"
                error={errors[DISPLAY_LANDLINE]?.message}
                isRequired={requiredFields.includes(DISPLAY_LANDLINE)}
              />
              <FormCheckbox
                name={ADS}
                control={form.control}
                label="Opt In Partner Online Ads "
                error={errors[ADS]?.message}
                isRequired={requiredFields.includes(ADS)}
              />
              <FormCheckbox
                name={PARTNER_EMAIL}
                control={form.control}
                label="Opt In Partner Emails"
                error={errors[PARTNER_EMAIL]?.message}
                isRequired={requiredFields.includes(PARTNER_EMAIL)}
              />
            </div>
          </div>
        </div>
        <Card className="shadow-none rounded-lg border-0 mt-[20px]">
          <CardContent className="grid grid-cols-1 gap-7">
            <FormSectionHeader className="flex flex-row gap-3">
              <div className="flex items-center justify-center p-2 border border-primary bg-blue-50 rounded-full h-10 w-10">
                <UsersRound className="stroke-primary"/>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-primary">Neighbor Contact Sharing</span>
                <span className="font-normal text-[12px]">Control whether your primary contact information can be shared with owners of neighboring properties.</span>
              </div>
            </FormSectionHeader>

            <div className="grid grid-cols-2 gap-3"> 
              <div>
                <FormSelect 
                  name={PROPERTY_ID}
                  control={form.control}
                  label="Select property for this preference"
                  placeholder="Please select"
                  options={myProperties?.map((data: any) => ({
                    label: `${data?.propertyName || ""} | ${data?.blockName || ""} | ${data?.zoneName || ""} | Unit ${data?.unitNumber || ""}`,
                    value: data?.propertyId?.toString(),
                  }))}
                />
              </div>
              <div className="flex flex-row gap-3 bg-blue-50 p-2 rounded-md">
                <div className="flex items-center justify-center">
                  <Info className="stroke-primary bg-blue-50 rounded-full h-5 w-5"/>
                </div>
                <span className="text-[12px]">Each property has separate settings. Changes here will only apply to the selected property.</span>
              </div>
            </div>

            {propertyId ? isLoadingConsentDtl ? <Loader /> :
              <>
                <div className="flex flex-row gap-3 bg-blue-50 p-2 rounded-md border border-blue-100">
                  <FormSwitch 
                    name={CONSENT_ENABLED}
                    control={form.control}
                  />
                  <div className="flex flex-col gap-1 w-[70%]">
                    <span className="text-[14px] font-semibold">Share my contact information with selected neighbors</span>
                    <span className="font-normal text-[12px]">Your primary name, email address, and mobile number will be visible only to owners of the properties you select.
                    You can update or withdraw your consent at any time.</span>
                  </div>
                  {formData[CONSENT_ENABLED] ? 
                  <div className="flex items-center justify-center">
                    <div className="bg-green-50 border border-green-600 text-green-600 p-2 rounded-sm text-[12px]">
                      Active Since {consentDtl?.updatedAt ? moment(consentDtl?.updatedAt).format("MM/DD/YYYY") : moment().format("MM/DD/YYYY")}
                    </div>
                  </div> : <></>}
                </div>

                <div>
                  <FormSelect 
                    key={neighborKey}
                    multiSelect
                    name={NEIGHBOR_PROPERTY_ID}
                    control={form.control}
                    label="Select upto 4 neighboring properties"
                    placeholder="Please select"
                    options={neighborProperties?.map((data: any) => ({
                      label: `${data?.propertyName || ""} | ${data?.blockName || ""} | ${data?.zoneName || ""} | Unit ${data?.unitNumber || ""}`,
                      value: data?.propertyId?.toString(),
                    }))}
                    showChip
                    disabled={isLoadingNeighborProperties}
                    onChange={onChangeNeighbor}
                  />
                </div>

                <div className="flex flex-row gap-3 bg-amber-50 p-2 rounded-md border border-amber-300">
                  <Lightbulb className="stroke-amber-700 h-6 w-6"/>
                  <div className="flex flex-col gap-1">
                    <span className=" text-[16px] font-semibold">Important</span>
                    <span className="font-normal text-[12px]">
                      <ul className="my-4 list-disc pl-4">
                        <li>Sharing is one-directional. You will only see another owner's contact information if they have separately selected your property and provided consent.</li>
                        <li>Only active, owned properties from the same community are shown.</li>
                        <li>You can change or withdraw your consent at any time.</li>
                      </ul>
                    </span>
                  </div>
                </div>

                <div className="flex flex-row gap-3 bg-blue-50 p-2 rounded-md border border-blue-100">
                  <ShieldCheck className="stroke-primary" />
                  <div className="flex flex-col gap-1">
                    <span className="text-[16px] font-semibold">Your privacy is important to us.</span>
                    <span className="font-normal text-[12px]">We never share your information without your consent.
                      All changes are tracked and audited.<br/>
                      <span
                        className="underline text-primary font-[600] cursor-pointer"
                        onClick={onTermsConditionClick}
                      >
                        Learn more about our privacy practices
                      </span>
                    </span>
                  </div>
                </div>
              </> : <></>
            }
          </CardContent>
        </Card>
        <div className="flex flex-row justify-end gap-3  py-3">
          <Button type="submit" disabled={isUpdatePreferencePending}>
            {isUpdatePreferencePending && <Loader variant="secondary" />} Save &
            Next
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step3;
